import { lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { lazyImport } from '@core/utils/lazyImport';
import ErrorPage from 'app/features/error/ErrorPage';
import ShareLinkRedirect from 'app/features/read-only-study/routes/ShareLinkRedirect';
import Redirect from 'app/features/redirect';
import AppRoot from './AppRoot';
import SettingsRoot from './SettingsRoot';

const { Landing } = lazyImport(() => import('../features/studies'), 'Landing');

// Only three routes are imported eagerly, and each for a specific reason:
//  - ErrorPage is the `errorElement` for all three top-level routes. Loading it
//    lazily would mean it can't render when a chunk fetch is what failed.
//  - ShareLinkRedirect and Redirect are top-level routes with no ancestor
//    <Suspense> (RouterProvider doesn't supply one), so a lazy element there
//    would throw. Both are tiny; ShareLinkRedirect carries its own <Suspense>
//    for its heavy child, which is the correct shape.
// Everything else is lazy. Route chunks matter for plain weight, but for the
// routes below it's load-bearing: they pull in heavy third-party UI runtimes
// whose modules register global side effects — e.g. inserted stylesheet rules —
// as soon as the module is evaluated, whether or not the route is ever visited:
//  - Study: the ~11MB OHIF viewer UMD.
//  - AddReport/AddTemplate/EditTemplate: the RoosterJS-based report/template
//    text editor, built on `@fluentui/react`. Fluent UI's `merge-styles`
//    registers a scroll-lock helper at import time under a class name
//    ("css-0") that collides with MUI's own reserved "empty sx" class of the
//    same name, leaking `overflow: hidden !important` onto unrelated MUI
//    `Box`/`Container` elements on every other page.
// Keeping these lazy confines that side effect to only the pages that need it.
// NB: importing any of them through a barrel that also re-exports an eager
// sibling silently defeats this — that is exactly how the viewer UMD used to
// end up in the main chunk (via features/read-only-study/index.ts, now gone).
const { Study } = lazyImport(() => import('app/features/study'), 'Study');
const { AddReport } = lazyImport(
  () => import('app/features/reports/add/routes/AddReport'),
  'AddReport',
);
const AddTemplate = lazy(() => import('app/features/templates/add/routes'));
const EditTemplate = lazy(() => import('app/features/templates/edit/routes'));
const { GetPermissions } = lazyImport(
  () => import('../features/permissions/get'),
  'GetPermissions',
);
const { AddPermission } = lazyImport(() => import('../features/permissions/add'), 'AddPermission');
const { EditPermission } = lazyImport(
  () => import('../features/permissions/edit'),
  'EditPermission',
);
const { GetReports } = lazyImport(() => import('../features/statistics/GetAll'), 'GetReports');
const { Security } = lazyImport(() => import('../features/security/index'), 'Security');
const { GetGroups } = lazyImport(() => import('../features/groups/routes/GetGroups'), 'GetGroups');
const { AddGroup } = lazyImport(() => import('../features/groups/routes/AddGroup'), 'AddGroup');
const { EditGroup } = lazyImport(() => import('../features/groups/routes/EditGroup'), 'EditGroup');
const { UserModalityDetail } = lazyImport(
  () => import('../features/statistics/UserModalityDetail'),
  'UserModalityDetail',
);
const { GeneralInterpretationDetails } = lazyImport(
  () => import('../features/statistics/GeneralInterpretationsDetails'),
  'GeneralInterpretationDetails',
);
// Pulls in react-colorful (theming color pickers); kept lazy to keep it out of
// the main bundle since it's only needed on this one settings page.
const Customization = lazy(() => import('app/features/customization/Customization'));
// Plain feature pages. These were eager; none of them is on the critical path
// for the study list, and two carried significant weight into the main chunk:
// Subscription was the only eager path to recharts, and EditProfile pulls in
// react-signature-canvas + react-image-crop. Both route shells already wrap
// <Outlet /> in <Suspense>, so no other change is needed.
const Subscription = lazy(() => import('app/features/subscription/routes/Subscription'));
const Invoices = lazy(() => import('app/features/subscription/routes/Invoices'));
const EditProfile = lazy(() => import('app/features/users/edit/routes/EditProfile'));
const Preferences = lazy(() => import('app/features/preferences/routes/Preferences'));
const ReferrersReport = lazy(
  () => import('app/features/statistics/GetAll/components/ReferrersReport'),
);
const Notifications = lazy(() => import('app/features/notifications/get/routes/NotificationPanel'));
const Templates = lazy(() => import('app/features/templates/routes/Templates'));
const Users = lazy(() => import('app/features/users/routes/Users'));
const AddUser = lazy(() => import('app/features/users/add/routes/AddUser'));
const EditUser = lazy(() => import('app/features/users/edit-user/routes/EditUser'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppRoot />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Landing />,
      },
      {
        path: 'studies/:studyInstanceUid',
        element: <Study />,
      },
      {
        path: 'perfil',
        element: <EditProfile />,
      },
      {
        path: 'preferences',
        element: <Preferences />,
      },
      {
        path: 'reports',
        element: <AddReport />,
      },
      {
        path: 'templates',
        element: <Templates />,
      },
      {
        path: 'templates/add',
        element: <AddTemplate />,
      },
      {
        path: 'templates/:templateId',
        element: <EditTemplate />,
      },
      {
        path: 'notifications',
        element: <Notifications />,
      },
      {
        path: '/settings/reports/referrers',
        element: <ReferrersReport />,
      },
    ],
  },
  {
    path: '/study/view',
    element: <ShareLinkRedirect />,
    errorElement: <ErrorPage notLogged />,
  },
  {
    path: '/redirect',
    element: <Redirect />,
    errorElement: <ErrorPage notLogged />,
  },
  {
    path: '/settings',
    element: <SettingsRoot />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'users',
        element: <Users />,
      },
      {
        path: 'users/add',
        element: <AddUser />,
      },
      {
        path: 'users/edit/:userUid',
        element: <EditUser />,
      },
      // These React routes are currently being commented out as they are just a mockup for now.
      // They will be enabled once the corresponding services for the full feature integration are in place.
      {
        path: 'reports',
        element: <GetReports />,
      },
      {
        path: 'reports/modalities/:userUid',
        element: <UserModalityDetail />,
      },
      {
        path: 'reports/modalities',
        element: <GeneralInterpretationDetails />,
      },
      {
        path: 'permissions',
        element: <GetPermissions />,
      },
      {
        path: 'permissions/add',
        element: <AddPermission />,
      },
      {
        path: 'permissions/edit/:permissionId',
        element: <EditPermission />,
      },
      {
        path: 'security',
        element: <Security />,
      },
      {
        path: 'subscription',
        element: <Subscription />,
      },
      {
        path: 'subscription/invoices',
        element: <Invoices />,
      },
      {
        path: 'groups',
        element: <GetGroups />,
      },
      {
        path: 'groups/add',
        element: <AddGroup />,
      },
      {
        path: 'groups/:groupId',
        element: <EditGroup />,
      },
      // {
      //   path: 'account',
      //   element: <p>Cuenta</p>,
      // },
      /* TODO: This section of code has been temporarily commented out as the feature it implements is being hidden for the time being. The decision to disable this feature comes as we are currently reassessing and clarifying our billing process. We intend to revisit and potentially re-enable this functionality once we have a more definitive understanding of the billing mechanisms and how they should be accurately implemented within our system. This measure ensures that we maintain the integrity and reliability of our billing operations while we make necessary adjustments.
      {
        path: 'subscription/change-subscription',
        element: <ChangeSubscription />,
      }, 
      */
      // {
      //   path: 'groups',
      //   element: <p>Grupos</p>,
      // },
      {
        path: 'edit',
        element: <Customization />,
      },
      // {
      //   path: 'support',
      //   element: <p>Soporte & Contacto</p>,
      // },
    ],
  },
]);

const AppRoutes = () => {
  return <RouterProvider router={router} />;
};

export default AppRoutes;
